import { useSuspenseQuery } from '@apollo/client';

import { SettingsGroup, SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  Field,
  FieldLabel,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Icon,
  Input,
  Separator,
  Stack,
  Text,
} from '@eluve/components';
import {
  ProviderDetailsSetting,
  SignatureSettings,
  SpecialtySettings,
  UserProfileSettings,
  userFragment,
  userSpecialtyFragment,
} from '@eluve/frontend-feature-user-settings';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

const getUserProfileQuery = graphql(
  `
    query getUserProfile($userId: uuid!) {
      usersByPk(id: $userId) {
        __typename
        id
        ...User
        ...UserSpecialty
      }
    }
  `,
  [userFragment, userSpecialtyFragment],
);

const buildBaaRequestMailto = (params: {
  tenantId: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) => {
  const { tenantId, userId, firstName, lastName, email } = params;
  const displayName = firstName
    ? `${firstName} ${lastName ?? ''}`.trim()
    : 'User';
  const userNameForBody = firstName
    ? `${firstName} ${lastName ?? ''}`.trim()
    : 'Not provided';
  const userEmailForBody = email ?? 'Not provided';

  const subject = `BAA Request - ${displayName} - Tenant ${tenantId}`;
  const bodyLines = [
    'Hello Eluve Team,',
    '',
    'I would like to request a signed Business Associates Agreement for our organization.',
    '',
    'Account Details:',
    `Tenant ID: ${tenantId}`,
    `User ID: ${userId}`,
    `User Name: ${userNameForBody}`,
    `User Email: ${userEmailForBody}`,
    '',
    'Thank you for your assistance.',
  ];

  return `mailto:hello+baa@eluve.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
};

export const UserSettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('user_settings');

  const { data } = useSuspenseQuery(getUserProfileQuery, {
    variables: { userId },
  });
  const user = data.usersByPk;

  return (
    <SettingsPageFrame title="User Settings">
      <SettingsGroup
        title="General"
        description="Your basic profile and account information."
      >
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldLabel className="whitespace-nowrap">Email</FieldLabel>
          <Input
            type="email"
            value={user?.email ?? ''}
            readOnly
            disabled
            className="max-w-60 bg-bg-primary"
          />
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldLabel className="whitespace-nowrap">Full Name</FieldLabel>
          <div className="flex w-full max-w-60 justify-end">
            <UserProfileSettings />
          </div>
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldLabel className="whitespace-nowrap">Specialty</FieldLabel>
          <div className="flex w-full max-w-60 justify-end">
            <SpecialtySettings />
          </div>
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldLabel className="whitespace-nowrap">Signature</FieldLabel>
          <div className="flex w-full max-w-60 justify-end">
            <SignatureSettings />
          </div>
        </Field>
      </SettingsGroup>

      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>Medical Provider Details</FormTitle>
          <FormNewDescription>
            Enter your license number, NPI number, and any other relevant
            details about your medical practice.
          </FormNewDescription>
        </FormHeader>
        <ProviderDetailsSetting />
      </FormSection>

      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>Business Associates Agreement</FormTitle>
        </FormHeader>
        <Stack gap={3} align="stretch">
          <Text size="sm" weight="medium" color="tertiary">
            We care about safeguarding your data. Your BAA is part of staying
            HIPAA compliant.
          </Text>
          <Text size="sm" weight="medium" color="tertiary">
            If your organization requires a separate, signed version of the BAA
            for record-keeping purposes, you can request one by emailing us at{' '}
            <a
              href={buildBaaRequestMailto({
                tenantId,
                userId,
                firstName: user?.firstName,
                lastName: user?.lastName,
                email: user?.email,
              })}
              className="underline underline-offset-2"
            >
              hello+baa@eluve.com
            </a>
            .
          </Text>
        </Stack>
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="pt-2"
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              window.open(
                'https://www.eluve.com/business-associate-agreement',
                '_blank',
              );
              analytics.capture('baa_view_clicked', { tenantId, userId });
            }}
          >
            <Icon name="SquareArrowOutUpRight" size="auto" />
            View BAA
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              window.open(
                '/baa/2025-09-04/BUSINESS_ASSOCIATE_AGREEMENT.pdf',
                '_blank',
              );
              analytics.capture('baa_download_clicked', { tenantId, userId });
            }}
          >
            <Icon name="Download" size="auto" />
            Download PDF
          </Button>
        </Stack>
      </FormSection>
    </SettingsPageFrame>
  );
};
